/**
 * Client-side face role inference.
 *
 * Sentinel emits a `role` field per detection ("player" | "referee" |
 * "spectator" | "unknown") in `meta.sentinel.json` (see
 * `sentinel/contracts.py:352`). Seraph face records DON'T carry the
 * field, so we cross-reference each face's `frame_bbox` against the
 * Sentinel person-body boxes.
 *
 * Coordinate spaces:
 *   - Face `frame_bbox` is in source pixels (e.g. 8640 × 5760).
 *   - Sentinel detection `box` is in its downscaled detection image
 *     (`meta.pipeline.sentinel.image_dims`, typically 2000 × 1333).
 *   We rescale Sentinel boxes up to source space before comparing.
 *
 * Multi-signal match (a face is a "player" only when ALL hold for the
 * best-overlap detection):
 *
 *   1. Containment ≥ 0.7 — at least 70 % of the face's pixel area
 *      sits inside the person box. Drops faces only grazing a body box.
 *   2. Head region — face center sits in the top 45 % of the person box.
 *      A player's head is always near the top of the body box; crowd
 *      faces that happened to fall inside (lower-body area) are
 *      excluded.
 *   3. Keypoint anchor (when keypoints are present) — the face must
 *      contain the detection's nose / eye / ear keypoints with
 *      confidence ≥ 0.5. This is the strongest signal: if Sentinel's
 *      head keypoints land inside the face box, that face IS the
 *      player. When keypoints are missing we fall back to (1) + (2).
 */

const HEAD_KEYPOINTS = [0, 1, 2, 3, 4]; // nose, L-eye, R-eye, L-ear, R-ear
const KEYPOINT_CONF_MIN = 0.5;
const CONTAINMENT_MIN = 0.7;
const HEAD_REGION_FRACTION = 0.45;

function intersectionArea(a, b) {
  const ix1 = Math.max(a[0], b[0]);
  const iy1 = Math.max(a[1], b[1]);
  const ix2 = Math.min(a[2], b[2]);
  const iy2 = Math.min(a[3], b[3]);
  const iw = Math.max(0, ix2 - ix1);
  const ih = Math.max(0, iy2 - iy1);
  return iw * ih;
}

function boxArea(b) {
  return Math.max(0, b[2] - b[0]) * Math.max(0, b[3] - b[1]);
}

function pointInBox(px, py, box) {
  return px >= box[0] && px <= box[2] && py >= box[1] && py <= box[3];
}

function keypointInsideFace(keypoints, faceBox, scaleX, scaleY) {
  if (!Array.isArray(keypoints)) return false;
  for (const idx of HEAD_KEYPOINTS) {
    const kp = keypoints[idx];
    if (!Array.isArray(kp) || kp.length < 3) continue;
    const conf = kp[2];
    if (conf < KEYPOINT_CONF_MIN) continue;
    const px = kp[0] * scaleX;
    const py = kp[1] * scaleY;
    if (pointInBox(px, py, faceBox)) return true;
  }
  return false;
}

export function inferFaceRole(face, detections, scaleX = 1, scaleY = 1) {
  const fb = face?.frame_bbox;
  if (!Array.isArray(fb) || fb.length !== 4) return 'unknown';
  if (!Array.isArray(detections) || detections.length === 0) return 'unknown';
  const faceArea = boxArea(fb);
  if (faceArea <= 0) return 'unknown';
  const faceCx = (fb[0] + fb[2]) / 2;
  const faceCy = (fb[1] + fb[3]) / 2;

  let best = { score: 0, role: 'unknown' };

  for (const d of detections) {
    const b = d?.box;
    if (!b) continue;
    const detBox = [
      b.x1 * scaleX,
      b.y1 * scaleY,
      b.x2 * scaleX,
      b.y2 * scaleY,
    ];
    const role = d.role || 'unknown';

    // Strongest signal: any high-confidence head keypoint inside the
    // face box → this face IS the player.
    if (keypointInsideFace(d.keypoints, fb, scaleX, scaleY)) {
      return role;
    }

    // Fallback geometric test: containment ≥ threshold AND face center
    // sits in the upper head-region of the body box.
    const inter = intersectionArea(fb, detBox);
    const contain = inter / faceArea;
    if (contain < CONTAINMENT_MIN) continue;

    if (!pointInBox(faceCx, faceCy, detBox)) continue;
    const detH = detBox[3] - detBox[1];
    const headCutoff = detBox[1] + detH * HEAD_REGION_FRACTION;
    if (faceCy > headCutoff) continue; // face is in the lower body — skip

    if (contain > best.score) best = { score: contain, role };
  }

  return best.score > 0 ? best.role : 'unknown';
}
